/** The workspace directory's credit and subscription columns, named once so the
 *  directory and the background warm share one cache entry per actor. Both are
 *  view reads over every company the directory lists, keyed by company id. */

import { getSupabaseClient } from '../../utils/supabase/client';
import { fetchSubscriptionStates, type CompanySubscriptionState } from '../../services/SubscriptionStateService';
import { currentBackgroundReadScope, peekBackgroundRead, type BackgroundReadScope } from '../../services/BackgroundReadCache';

// Shape returned by the v_company_credit_balance view. Kept local so we don't
// have to regenerate global database types just for this feature.
export interface CreditBalance {
  company_id: string;
  // `null` when the client is on an unlimited plan (Platinum). The UI
  // renders "Unlimited" rather than a number in that case.
  plan_monthly_credits: number | null;
  topup_credits: number;
  consumed_this_period: number;
  remaining: number | null;
  is_unlimited: boolean;
  billing_period: string;
}

export interface WorkspaceCreditColumns {
  creditBalances: Record<string, CreditBalance>;
  subscriptionStates: Record<string, CompanySubscriptionState>;
}

export const workspaceCreditColumnsRead = (companyIds: string[]) => ({
  name: 'workspaces/credit-columns',
  load: async (): Promise<WorkspaceCreditColumns> => {
    // The view already filters top-ups to the current billing period and sums
    // ledger entries, so the directory renders what it returns.
    const { data, error } = await getSupabaseClient()
      .from('v_company_credit_balance')
      .select('company_id, plan_monthly_credits, topup_credits, consumed_this_period, remaining, is_unlimited, billing_period')
      .in('company_id', companyIds);
    if (error) throw new Error(error.message);
    const creditBalances: Record<string, CreditBalance> = {};
    (data || []).forEach((row) => { creditBalances[(row as CreditBalance).company_id] = row as CreditBalance; });
    // Same trigger as the balances, so the licence badge and the credit figure
    // can never disagree about which period they describe.
    return { creditBalances, subscriptionStates: await fetchSubscriptionStates(companyIds) };
  },
});

/** The directory spans every workspace, but a cache entry needs one: the user's own. */
export function workspaceCreditScope(user: { workspaceId?: string | null; workspace_id?: string | null } | null | undefined): BackgroundReadScope | null {
  return currentBackgroundReadScope(user?.workspaceId ?? user?.workspace_id ?? undefined);
}

export function warmedWorkspaceCreditColumns(scope: BackgroundReadScope | null): WorkspaceCreditColumns | undefined {
  return peekBackgroundRead<WorkspaceCreditColumns>(scope, workspaceCreditColumnsRead([]).name)?.data;
}
